import { computed, ref } from 'vue'
import supplierHttp, { setUnauthorizedHandler } from '@supplier/http/supplierHttp'
import { defineStore } from 'pinia'

import { SUPPLIER_API } from '@supplier/http/api-routes'

import { broadcastLogout, clearAuthCookie, startAuthWatcher } from '@/utils/authWatcher'
import { SUPPLIER_ROLE_ID } from '@supplier/consts'

import { userpilot } from '@/services/userpilot'

export const useSupplierSessionStore = defineStore('supplier-session', () => {
  const user = ref(null)
  const loadState = ref('idle')
  let pendingFetch = null
  let stopAuthWatcher = null
  let isRedirecting = false
  let isLoggingOut = false

  const isSupplier = computed(() => Number(user.value?.role_id) === SUPPLIER_ROLE_ID)
  const supplier = computed(() => user.value?.supplier ?? null)
  const displayName = computed(() => supplier.value?.name || user.value?.name || '')
  const stores = computed(() => (Array.isArray(user.value?.store) ? user.value.store : []))
  const store = computed(() => stores.value[0] ?? null)
  const hasStore = computed(() => stores.value.length > 0)

  function redirectToLogin() {
    if (isRedirecting) return
    isRedirecting = true
    const publicUrl = window.config?.public_url || ''
    window.location.assign(`${publicUrl}/login?continue=${encodeURIComponent(window.location.href)}`)
  }

  function handleUnauthorized() {
    if (isLoggingOut) return false
    if (isRedirecting) return true
    if (!user.value) return false
    user.value = null
    clearAuthCookie()
    redirectToLogin()
    return true
  }

  function onSessionStarted() {
    const { id, name, email, created_at: createdAt } = user.value
    userpilot.identify(id, { name, email, created_at: createdAt })
    if (stopAuthWatcher) return
    stopAuthWatcher = startAuthWatcher({
      isLoggedIn: () => Boolean(user.value),
      onForeignLogout: () => {
        user.value = null
        redirectToLogin()
      },
    })
  }

  async function fetchUser() {
    loadState.value = 'loading'
    try {
      const { data } = await supplierHttp.get(SUPPLIER_API.user)
      if (!data || typeof data !== 'object') throw new Error('Unexpected user payload')
      user.value = data
      loadState.value = 'ready'
      onSessionStarted()
      return data
    } catch (error) {
      user.value = null
      loadState.value = error?.response?.status === 401 ? 'unauthorized' : 'error'
      return null
    }
  }

  function ensureUser() {
    if (user.value) return Promise.resolve(user.value)
    pendingFetch ??= fetchUser().finally(() => {
      pendingFetch = null
    })
    return pendingFetch
  }

  function updateUser(nextUser) {
    if (!nextUser) return
    user.value = { ...nextUser, store: user.value?.store ?? nextUser.store }
  }

  async function logout() {
    if (isLoggingOut) return
    isLoggingOut = true
    try {
      await supplierHttp.post(SUPPLIER_API.logout)
    } catch (error) {
      console.error(error)
    }
    user.value = null
    clearAuthCookie()
    broadcastLogout()
    window.location.assign(window.config?.public_url || '/')
  }

  setUnauthorizedHandler(handleUnauthorized)

  return {
    user,
    loadState,
    isSupplier,
    supplier,
    displayName,
    stores,
    store,
    hasStore,
    fetchUser,
    ensureUser,
    updateUser,
    logout,
    redirectToLogin,
  }
})
